import React, { useEffect, useMemo, useState } from 'react'
import { ReactComponent as GoNextIcon } from '../../../assets/svg/GoNextIcon.svg'
import { ReactComponent as SearchBlue } from '../../../assets/svg/SearchBlue.svg'
import SearchModal from './SearchModal'
import { searchStudentForAnalytics } from '../../../server/server'
import { useHistory } from 'react-router-dom/cjs/react-router-dom'
import { useTranslation } from 'react-i18next'
import InfoButton from '../../common/InfoButton'
import { useHasStudentInPath, useQuery } from '../../../utils/util'

function SingleAnalyticsHeader({ showSearch = true, showInfo = true }) {
    const { t, locale } = useTranslation()
    const history = useHistory()
    const query = useQuery()
    const currentId = query.get('id')
    const isStudentAnalytics = useHasStudentInPath()
    const [searchText, setSearchText] = useState('')
    const [isFocused, setIsFocused] = useState(false)
    const [showSearchResults, setShowSearchResults] = useState(false)
    const [loading, setLoading] = useState(false)
    const [initLoading, setInitLoading] = useState(true)
    const [results, setResults] = useState([])
    const [hasMore, setHasMore] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [total, setTotal] = useState(0)

    const inputBackground = useMemo(() => {
        return isFocused || !!searchText ? 'bg-white' : 'bg-[#F3EFFF]'
    }, [isFocused, searchText])

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchResults(searchText, 0)
        }, 400)
        return () => clearTimeout(delayDebounceFn)
    }, [searchText])

    const loadMore = () => {
        if (!loadingMore && !loading) {
            setLoadingMore(true)
            fetchResults(searchText, results?.length ?? 0)
        }
    }

    const fetchResults = async (searchTerm, resultsNum) => {
        const data = {
            searchText: searchTerm.toLowerCase(),
            loadedData: resultsNum,
        }
        if (!isStudentAnalytics) {
            data['classId'] = currentId
        }
        const resp = await searchStudentForAnalytics(data)
        if (resp.status === 200) {
            const array = resp?.data?.results
            const size = resp?.data?.total_count?.count
            if (array) {
                if (resultsNum === 0) {
                    setResults(array)
                } else {
                    setResults((prev) => [...prev, ...array])
                }
                setTotal(size)
                setHasMore(size > results?.length)
            } else {
                setResults([])
                setTotal(0)
            }
        } else {
            setResults([])
            setTotal(0)
        }
        setLoading(false)
        setLoadingMore(false)
    }

    const changeText = (e) => {
        setSearchText(e.target.value)
        setShowSearchResults(true)
        if (initLoading) {
            setInitLoading(false)
            setLoading(true)
        }
    }

    const closeModal = () => setShowSearchResults(false)
    const goBack = () => history.goBack()

    return (
        <div className="w-full flex flex-row items-center justify-between ">
            <button className="flex flex-row items-center justify-center gap-[10px] bg-white rounded-full  px-[15px] h-[40px]" onClick={goBack}>
                <GoNextIcon />
                <p className="text-[16px] leading-5 text-deepPurple font-semibold">{t('back')}</p>
            </button>
            {showSearch && (
                <div className="w-[600px] flex items-center justify-center relative py-[4px] -my-[4px]" onMouseLeave={() => setShowSearchResults(false)}>
                    <div className={`flex flex-row items-center justify-center gap-[6px] border border-[#BEBECB] rounded-full h-[44px] px-[20px] ${inputBackground} relative`}>
                        <input
                            type="text"
                            placeholder={t('Search for students')}
                            className="bg-[transparent] placeholder:text-mediumGray leading-5 text-[16px] text-deepPurple font-medium w-[220px]"
                            value={searchText}
                            onChange={changeText}
                            onFocus={() => {
                                setIsFocused(true)
                                setShowSearchResults(true)
                            }}
                            onBlur={() => setIsFocused(false)}
                        />
                        <SearchBlue />
                    </div>
                    {showSearchResults && (
                        <SearchModal
                            closModal={closeModal}
                            loading={loading}
                            dataLength={results?.length}
                            hasMore={hasMore}
                            loadNext={loadMore}
                            results={results}
                            total={total}
                            loadingMore={loadingMore}
                        />
                    )}
                </div>
            )}
            {showInfo && (
                <div>
                    <InfoButton text={t('Use this search to find a student for deeper and advanced analytics')} />
                </div>
            )}
        </div>
    )
}

export default SingleAnalyticsHeader
